/**
 * Tiny inline-SVG sparkline / bar chart used on the team detail page to
 * show attendance (player count) over time. No chart library — just a
 * handful of <rect>/<polyline> elements scaled to the data range.
 */

interface Props {
  values: (number | null)[];
  // 'bars' for attendance counts, 'line' for placement trends.
  mode?: 'bars' | 'line';
  // Lower values are "better" (e.g. placement). Inverts the line fill hint.
  width?: number;
  height?: number;
  label?: string;
  color?: string;
}

const Sparkline: React.FC<Props> = ({
  values,
  mode = 'bars',
  width = 260,
  height = 48,
  label,
  color = 'var(--crimson-9)',
}) => {
  const nums = values.map((v) => (v === null || v === undefined ? 0 : v));
  const present = values.map((v) => v !== null && v !== undefined);
  const max = Math.max(1, ...nums);
  const n = nums.length;

  if (n === 0) {
    return (
      <div className="teams-spark">
        {label && (
          <div style={{ fontSize: 11, color: 'var(--gray-11)', marginBottom: 4 }}>{label}</div>
        )}
        <div style={{ fontSize: 12, color: 'var(--gray-10)' }}>No data</div>
      </div>
    );
  }

  const pad = 2;
  const innerW = width - pad * 2;
  const innerH = height - pad * 2;

  return (
    <div className="teams-spark">
      {label && (
        <div style={{ fontSize: 11, color: 'var(--gray-11)', marginBottom: 4 }}>{label}</div>
      )}
      <svg
        className="teams-spark"
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={label ?? 'sparkline'}
      >
        {mode === 'bars'
          ? nums.map((v, i) => {
              const bw = innerW / n;
              const x = pad + i * bw;
              const h = present[i] ? Math.max(2, (v / max) * innerH) : 0;
              const y = pad + (innerH - h);
              return (
                <rect
                  key={i}
                  x={x + bw * 0.15}
                  y={y}
                  width={bw * 0.7}
                  height={h}
                  rx={1}
                  fill={color}
                  opacity={present[i] ? 0.85 : 0.25}
                />
              );
            })
          : (() => {
              const pts = nums.map((v, i) => {
                const x = n === 1 ? pad + innerW / 2 : pad + (i / (n - 1)) * innerW;
                const y = pad + (innerH - (v / max) * innerH);
                return `${x},${y}`;
              });
              return (
                <polyline
                  points={pts.join(' ')}
                  fill="none"
                  stroke={color}
                  strokeWidth={1.5}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              );
            })()}
      </svg>
    </div>
  );
};

export default Sparkline;
